<template>
  <div class="product-family-bundling-manager">
    <table
      class="uk-table uk-table-divider uk-margin-remove"
      v-if="bundlings.length"
    >
      <thead>
        <tr>
          <th class="uk-table-shrink"></th>
          <th colspan="2">{{$t('fields.name')}}</th>
        </tr>
      </thead>
      <draggable
        :options="{ handle: '.drag-handle' }"
        @change="moveBundling"
        element="tbody"
        v-model="bundlings"
      >
        <tr
          :key="bundling.id"
          class="product-family-bundling-manager__row"
          v-for="bundling in bundlings"
        >
          <td class="uk-table-shrink">
            <drag-handle :disabled="bundlings.length <= 1" />
          </td>
          <td>
            <router-link
              :to="{ name: 'product-family',
                                params: { id: bundling.productFamily.id } }"
            >{{bundling.productFamily.name}}</router-link>
          </td>
          <td>
            <data-grid-actions
              :editable="false"
              @remove="deleteBundlingWhenConfirmed(bundling)"
            />
          </td>
        </tr>
      </draggable>
      <tfoot>
        <tr>
          <td
            class="uk-padding-remove-left uk-padding-remove-right"
            colspan="3"
          >
            <common-button
              @click="$refs.bundlingFormDialog.show()"
              icon="plus"
              type="primary"
            >{{$t('actions.add')}}</common-button>
          </td>
        </tr>
      </tfoot>
    </table>

    <div class="uk-placeholder uk-text-center uk-margin-remove" v-else>
      <div class="uk-margin-bottom">{{$t('hints.add')}}</div>
      <common-button
        @click="$refs.bundlingFormDialog.show()"
        icon="plus"
        type="primary"
      >{{$t('actions.add')}}</common-button>
    </div>

    <product-family-bundling-form
      :errors="errors"
      @submit="insertBundling"
      ref="bundlingFormDialog"
      title="Produktfamilie hinzufügen"
    />
  </div>
</template>

<script>
import UIkit from 'uikit';
import Draggable from 'vuedraggable';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import deleteProductFamilyBundlingMutation
  from '@/api/mutations/delete-product-family-bundling.graphql';
import DragHandle from '@/components/DragHandle';
import insertProductFamilyBundlingMutation
  from '@/api/mutations/insert-product-family-bundling.graphql';
import ProductFamilyBundlingForm from '@/components/ProductFamilyBundlingForm';
import productFamilyQuery from '@/api/queries/product-family.graphql';
import Submittable from '@/mixins/Submittable';
import Toast from '@/helpers/Toast';
import updateProductFamilyBundlingMutation
  from '@/api/mutations/update-product-family-bundling.graphql';

export default {
  name: 'product-family-bundling-manager',
  mixins: [Submittable],
  components: {
    CommonButton,
    DataGridActions,
    Draggable,
    DragHandle,
    ProductFamilyBundlingForm,
  },
  props: {
    productFamily: Object,
  },
  data() {
    return {
      bundlings: (this.productFamily.bundlings || []).slice(0),
    };
  },
  watch: {
    productFamily(productFamily) {
      this.bundlings = (productFamily.bundlings || []).slice(0);
    },
  },
  computed: {
    bundleFamilyId() {
      return this.productFamily.id;
    },
  },
  methods: {
    async insertBundling(formData) {
      const bundling = Object.assign({}, formData, {
        bundleFamilyId: this.bundleFamilyId,
      });
      const operation = await this.submitWithMutation({
        mutation: insertProductFamilyBundlingMutation,
        variables: { bundling },
        refetchQueries: [
          {
            query: productFamilyQuery,
            variables: { id: this.bundleFamilyId },
          },
        ],
      });
      if (operation.isValid) {
        const productFamily = operation.bundling.productFamily;
        const message = this.$t('toasts.created', { name: productFamily.name });
        this.$refs.bundlingFormDialog.hide();
        Toast.success(message);
      }
    },
    moveBundling({ moved }) {
      const bundlingId = moved.element.id;
      const position = moved.newIndex + 1;
      return this.$apollo.mutate({
        mutation: updateProductFamilyBundlingMutation,
        variables: { id: bundlingId, bundling: { position } },
        refetchQueries: [
          {
            query: productFamilyQuery,
            variables: { id: this.bundleFamilyId },
          },
        ],
      });
    },
    deleteBundlingWhenConfirmed(bundling) {
      const message = this.$t('confirmations.delete',
                              { name: bundling.productFamily.name });
      UIkit.modal
           .confirm(message)
           .then(() => { this.deleteBundling(bundling); })
           .catch(() => {});
    },
    async deleteBundling(bundling) {
      await this.$apollo.mutate({
        mutation: deleteProductFamilyBundlingMutation,
        variables: { id: bundling.id },
        refetchQueries: [
          {
            query: productFamilyQuery,
            variables: { id: this.bundleFamilyId },
          },
        ],
      });
      const message = this.$t('toasts.deleted',
                              { name: bundling.productFamily.name });
      Toast.success(message);
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          add: 'Produktfamilie hinzufügen',
        },
        confirmations: {
          delete: `Produktfamilie <strong>{name}</strong> wirklich aus der
                   Bundlefamilie entfernen?`,
        },
        hints: {
          add: `Bitte füge eine Produktfamilie zu der Bundlefamilie hinzu. Du
                kannst so bestimmen, aus welchen Produkten Bundles bestehen
                dürfen, die zur aktuellen Bundlefamilie gehören.`,
        },
        validity: {
          unlimited: 'unbegrenzt',
        },
        toasts: {
          created: 'Produktfamilie zu Bundle hinzugefügt: {name}',
          deleted: 'Produktfamilie aus Bundle entfernt: {name}',
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../theme/variables.scss";

.product-family-bundling-manager__row {
  user-select: none;
  transition: {
    duration: 250ms;
    property: box-shadow;
    timing-function: ease-in-out;
  }

  &.sortable-ghost {
    box-shadow: 0 0 10px rgba(black, 0.15);
  }
}
</style>



// WEBPACK FOOTER //
// ProductFamilyBundlingManager.vue?0f0cf9d3